import type * as hast from 'hast';
import pMap from 'p-map';

import type { ContentRef } from '@gitbook/api-client';
import {
    BLOCK_PARAGRAPH,
    BLOCK_TABLE,
    type BlockTableData,
    type TableColumnDefinition,
    type TableDefinitionImageRecord,
    type TableView,
    table,
} from '@gitbook/doc-core';
import type { Block } from '@gitbook/slate';
import { assertIsDefined, assertNever, filterOutNullable } from '@gitbook/util-ts';

import type { FormattingOptions } from '../options';
import type { GastToHastTransformMap } from './types';

/**
 * Convert a table block into HTML nodes.
 */
export const transformBlocksTable: GastToHastTransformMap<Block> = {
    [BLOCK_TABLE]: async (node, eat, options) => {
        const data = node.data.toObject<BlockTableData>();

        // All columns are displayed, but we start with the visible ones
        const firstColumnIds = data.view.columns;
        const otherColumnIds = Object.keys(data.definition).filter(
            (key) => !firstColumnIds.includes(key)
        );
        const orderedColumns = [...firstColumnIds, ...otherColumnIds];

        const headerRow: hast.Element = {
            type: 'element',
            tagName: 'thead',
            children: [
                {
                    type: 'element',
                    tagName: 'tr',
                    children: orderedColumns.map((colId) => {
                        const colDef = data.definition[colId];

                        const properties: hast.Properties = getTableColumnViewAttributes(
                            data.view,
                            colDef
                        );
                        if (colDef.type !== 'text' && colDef.type !== 'select') {
                            properties['data-type'] = colDef.type;
                        }

                        // Pass options of columns as attributes
                        switch (colDef.type) {
                            case 'text':
                                // only add the property if it's not the default
                                if (colDef.textAlignment !== 'left') {
                                    properties.align = colDef.textAlignment;
                                }
                                if (typeof colDef.verticalAlignment !== 'undefined') {
                                    properties.valign = colDef.verticalAlignment;
                                }
                                break;
                            case 'rating':
                                properties['data-max'] = colDef.max;
                                break;
                            case 'users':
                                properties['data-multiple'] = colDef.multiple;
                                break;
                        }

                        const children: hast.Element['children'] = [
                            {
                                type: 'text',
                                value: data.definition[colId].title,
                            },
                        ];

                        // Add extra children for some specific column types
                        if (colDef.type === 'select') {
                            children.push({
                                type: 'element',
                                tagName: 'select',
                                properties: {
                                    ...(colDef.multiple ? { multiple: colDef.multiple } : {}),
                                },
                                children: colDef.options.map((option) => ({
                                    type: 'element',
                                    tagName: 'option',
                                    properties: {
                                        value: option.value,
                                        label: option.label,
                                        color: option.color,
                                    },
                                    children: [],
                                })),
                            });
                        }

                        return {
                            type: 'element',
                            tagName: 'th',
                            properties,
                            children,
                        };
                    }),
                    properties: {},
                },
            ],
            properties: {},
        };

        const recordKeys = table.defaultRecordSortedKeys(data);
        const rows: hast.Element[] = [];

        for (const recordKey of recordKeys) {
            const record = data.records[recordKey];

            rows.push({
                type: 'element',
                tagName: 'tr',
                children: await Promise.all(
                    orderedColumns.map(async (colId) => {
                        const colDef = data.definition[colId];
                        const value = record.values[colId];
                        let children: (hast.Element | hast.Text)[] = [];

                        switch (colDef.type) {
                            case 'text': {
                                const fragment = node.getFragmentByKey(value as string);
                                assertIsDefined(fragment);
                                const onlyChild =
                                    fragment.nodes.size === 1 ? fragment.nodes.get(0) : null;

                                // Table cells don't need a <p> tag (if there's only one, and it's a paragraph).
                                children =
                                    onlyChild && onlyChild.type === BLOCK_PARAGRAPH
                                        ? await eat(onlyChild.nodes)
                                        : await eat(fragment.nodes);
                                break;
                            }
                            case 'rating':
                            case 'checkbox':
                            case 'number':
                                children = [
                                    {
                                        type: 'text',
                                        value: String(value),
                                    },
                                ];
                                break;
                            case 'select':
                                if (!Array.isArray(value)) {
                                    // shouldn't happen
                                    break;
                                }
                                children = value
                                    .map((key, index) => {
                                        const option = colDef.options.find((k) => k.value === key);
                                        if (!option) {
                                            return null;
                                        }

                                        return {
                                            type: 'element',
                                            tagName: 'span',
                                            properties: {
                                                'data-option': key,
                                            },
                                            children: [
                                                {
                                                    type: 'text',
                                                    value: `${option.label}${
                                                        index !== value.length - 1 ? ', ' : ''
                                                    }`,
                                                },
                                            ],
                                        } as hast.Element;
                                    })
                                    .filter(filterOutNullable);
                                break;
                            case 'content-ref':
                                // @ts-ignore - value is a content ref
                                children = await formatContentReferencesAsLink([value], options);
                                break;
                            case 'users':
                                children = await formatContentReferencesAsLink(
                                    (value as string[]).map((userId) => ({
                                        kind: 'user',
                                        user: userId,
                                    })),
                                    options
                                );
                                break;
                            case 'files':
                                children = await formatContentReferencesAsLink(
                                    (value as string[]).map((fileId) => ({
                                        kind: 'file',
                                        file: fileId,
                                    })),
                                    options
                                );
                                break;
                            case 'image': {
                                const imageValue = value as TableDefinitionImageRecord;
                                if (imageValue) {
                                    // Extract ContentRef from either format
                                    const contentRef =
                                        typeof imageValue === 'object' && 'ref' in imageValue
                                            ? imageValue.ref
                                            : imageValue;
                                    children = await formatContentReferencesAsLink(
                                        [contentRef as ContentRef],
                                        options
                                    );
                                } else {
                                    children = [];
                                }
                                break;
                            }
                            default:
                                assertNever(colDef);
                        }

                        const tdProperties: hast.Properties = {
                            align:
                                // only add the alignment if it's not the default
                                colDef.type === 'text' && colDef.textAlignment !== 'left'
                                    ? colDef.textAlignment
                                    : undefined,
                            valign: (() => {
                                if (colDef.type !== 'text') {
                                    return undefined;
                                }

                                if (typeof colDef.verticalAlignment !== 'undefined') {
                                    return colDef.verticalAlignment;
                                }

                                return undefined;
                            })(),
                        };

                        // Add object-fit and alt data attribute for cover image columns
                        if (colDef.type === 'image' && value) {
                            const imageValue = value as TableDefinitionImageRecord;
                            const isCoverColumn =
                                data.view.type === 'cards' &&
                                (data.view.coverDefinition === colDef.id ||
                                    data.view.coverDefinitionDark === colDef.id);

                            if (isCoverColumn && typeof imageValue === 'object') {
                                if ('objectFit' in imageValue && imageValue.objectFit) {
                                    tdProperties['data-object-fit'] = imageValue.objectFit;
                                }
                                if ('alt' in imageValue && imageValue.alt) {
                                    tdProperties['data-alt'] = imageValue.alt;
                                }
                            }
                        }

                        return {
                            type: 'element',
                            tagName: 'td',
                            properties: tdProperties,
                            children,
                        } as hast.Element;
                    })
                ),
                properties: {},
            });
        }

        return [
            {
                type: 'element',
                tagName: 'table',
                properties: {
                    ...getTableViewAttributes(data.view),
                    ...(data.view.type !== 'grid' ? { 'data-view': data.view.type } : {}),
                    ...(typeof data.fullWidth !== 'undefined'
                        ? { dataFullWidth: `${Boolean(data.fullWidth)}` }
                        : undefined),
                },
                children: [
                    headerRow,
                    {
                        type: 'element',
                        tagName: 'tbody',
                        children: rows,
                        properties: {},
                    },
                ],
            },
        ];
    },
};

/**
 * Compute attributes to serialize on the <table> for a given view.
 */
function getTableViewAttributes(view: TableView): hast.Properties {
    switch (view.type) {
        case 'grid': {
            return {
                ...(view.hideHeader ? { 'data-header-hidden': true } : {}),
            };
        }
        case 'cards': {
            return {
                ...(view.cardSize !== 'medium' ? { 'data-card-size': view.cardSize } : {}),
                ...(view.hideColumnTitle ? { 'data-column-title-hidden': true } : {}),
            };
        }
        default:
            assertNever(view);
    }
}

/**
 * Compute attributes to serialize on the <th> for a given view and column.
 */
function getTableColumnViewAttributes(
    view: TableView,
    column: TableColumnDefinition
): hast.Properties {
    switch (view.type) {
        case 'grid': {
            const properties: hast.Properties = {};

            if (!view.columns.includes(column.id)) {
                properties['data-hidden'] = true;
            }

            if (view.columnWidths && column.id in view.columnWidths) {
                properties.width = view.columnWidths[column.id];
            }

            return properties;
        }
        case 'cards': {
            const properties: hast.Properties = {};

            if (!view.columns.includes(column.id)) {
                properties['data-hidden'] = true;
            }

            if (view.coverDefinition === column.id) {
                properties['data-card-cover'] = true;
            }

            if (view.coverDefinitionDark === column.id) {
                properties['data-card-cover-dark'] = true;
            }

            if (view.targetDefinition === column.id) {
                properties['data-card-target'] = true;
            }

            return properties;
        }
        default:
            assertNever(view);
    }
}

/**
 * Convert a list of content references into a list of links.
 */
async function formatContentReferencesAsLink(
    contentRefs: Array<ContentRef | null>,
    options: FormattingOptions
): Promise<hast.Element[]> {
    const children = await pMap(contentRefs, async (contentRef) => {
        if (!contentRef) {
            return null;
        }

        const resolved = await options.resolveContentRef(contentRef);

        if (resolved) {
            const element: hast.Element = {
                type: 'element',
                tagName: 'a',
                properties: {
                    href: resolved.url,
                },
                children: [
                    {
                        type: 'text',
                        value: resolved.text,
                    },
                ],
            };

            return element;
        }

        return null;
    });

    return children.filter(filterOutNullable);
}
